import {
  Add,
  Delete,
  Edit,
  Female,
  Male,
  Pets,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  QrCode as QrCodeIcon,
  Visibility as VisibilityIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fab,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  Collapse,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useState, useEffect } from "react";
import {
  VET_ANIMAL_SPECIES,
  VET_GENDER_STATUS,
  VET_ANIMAL_COLORS,
  getSpeciesByCode,
} from "@/types/VetTypes";
import { useRelatedPersons } from "@/hooks/session/useRelatedPersons";
import { useFluxVetSession } from "@/fluxvet/contexts/FluxVetSessionContext";
import { UFValueSet } from "@/utils/useValueSet";
import { PostVetPatient } from "@/services/fhir/vetpatient/PostVetPatient";
import { PutVetPatient } from "@/services/fhir/vetpatient/PutVetPatient";
import { PatchVetPatient } from "@/services/fhir/vetpatient/PatchVetPatient";
import { PostRelatedPerson } from "@/services/fhir/relatedperson/PostRelatedPerson";
import { PatchPatient } from "@/services/fhir/patient/PatchPatient";
import type { IRelatedPersonData, IVetPatientData } from "@/utils/interfaces";
import { endpoints, oids } from "@/configs/Settings";
import { HTTPResponseStatus } from "@/utils/enum";
import { toast } from "react-toastify";
import PetFormModal, {
  IPetFormData as IPetForm,
} from "@/fluxvet/components/PetFormModal";
import ConfirmDeleteModal from "./ConfirmDeleteModal";

import { generatePetCard, IPetCardData } from "@/fluxvet/utils/petCardUtils";
import { QRCodeCanvas } from "qrcode.react";
import { useSessionContext } from "@/contexts/SessionContext";
import { downloadFile } from "@/utils/fileDownloadHelper";
import { useNavigate } from "react-router-dom";

interface IPet {
  resourceType: string;
  id: string;
  meta: {
    versionId: string;
    lastUpdated: string;
    source: string;
  };
  identifier: {
    type: {
      coding: {
        system: string;
        code: string;
      }[];
    };
    system: string;
    value: string;
  }[];
  active: boolean;
  name: {
    use: string;
    text: string;
  }[];
  telecom?: {
    system: string;
    value: string;
  }[];
  gender: string;
  birthDate: string;
  species: string;
  breed?: string;
  color?: string;
  neutered?: boolean;
  photo?: string;
  photoBinaryId?: string;
  city?: string;
  state?: string;
  genderStatusCode?: string;
}

// Interface importada do PetFormModal (IPetForm)
const MyPets: React.FC = () => {
  // Contexto do FluxVet para obter os dados do usuário (tutor)
  const { user, userPatient } = useSessionContext();
  const navigate = useNavigate();

  // Hook para buscar os pets relacionados ao tutor
  const {
    patientRelated,
    loading: relatedLoading,
    fetchRelatedData,
  } = useRelatedPersons({
    identifier: user?.username,
  });

  const [selectedPet, setSelectedPet] = useState<IPet | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<"add" | "edit">("add");
  const [expandedPets, setExpandedPets] = useState<Set<string>>(new Set());

  // Estado para o modal de confirmação de exclusão
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [petToDelete, setPetToDelete] = useState<IPet | null>(null);
  const [photoWasRemoved, setPhotoWasRemoved] = useState(false);

  // Estado para o formulário (sincronizado com SignupVet)
  const [petForm, setPetForm] = useState<IPetForm>({
    name: "",
    birthDate: null,
    color: null,
    state: null,
    city: "",
    species: null,
    breed: null,
    sex: "",
    genderStatus: null,
  });

  const [loading, setLoading] = useState(false);

  const [downloadLoading, setDownloadLoading] = useState(false);

  // Foto do Pet (adicionar/editar)
  const [petPhotoBase64, setPetPhotoBase64] = useState<string | null>(null);
  const [petPhotoPreviewUrl, setPetPhotoPreviewUrl] = useState<string | null>(
    null
  );

  // Pré-visualização da carteirinha
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [previewPet, setPreviewPet] = useState<IPet | null>(null);
  const [petCardData, setPetCardData] = useState<IPetCardData | null>(null);

  // Abre pré-visualização da carteirinha e gera imagem
  const handlePreviewPetCard = async (pet: IPet) => {
    setPreviewPet(pet);
  };

  const generatePetCardData = async (pet: IPet, qrCodeBase64: string) => {
    try {
      setPreviewLoading(true);
      setPreviewOpen(true);

      const petData = {
        id: pet?.id,
        name: pet?.name[0].text,
        species: getSpeciesByCode(pet?.species)?.display || pet?.species,
        breed: pet?.breed || "Não informado",
        birthDate: pet?.birthDate,
        gender: pet?.gender === "male" ? "Macho" : "Fêmea",
        identifier: pet?.identifier?.[0]?.value || pet?.id,
        owner: userPatient?.name?.[0]?.text || "Tutor",
        createdAt: new Date().toISOString(),
        qrCodeBase64: qrCodeBase64,
      };
      setPetCardData(petData);

      const canvas = await generatePetCard(petData);
      const dataUrl = canvas.toDataURL("image/png");
      setPreviewImageUrl(dataUrl);
    } catch (error) {
      console.error("Erro ao gerar pré-visualização da carteirinha:", error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const generateBase64QRCode = (id: any) => {
    const canvas = document.getElementById(id)?.querySelector("canvas");
    if (canvas) {
      const base64 = canvas.toDataURL("image/png");
      return base64;
    }
  };

  useEffect(() => {
    const qrCodeBase64 = generateBase64QRCode(`qrcode-${previewPet?.id}`);
    if (previewPet && qrCodeBase64) {
      generatePetCardData(previewPet, qrCodeBase64);
    }
  }, [previewPet]);

  // Busca os pets relacionados quando o userPatient estiver disponível
  useEffect(() => {
    if (userPatient) {
      fetchRelatedData(userPatient);
    }
  }, [userPatient, fetchRelatedData]);

  // Converte patientRelated para o formato IPet[] usado pelo componente
  // Filtra apenas pets ativos (active: true)
  const pets: IPet[] =
    patientRelated
      ?.map((patient) => {
        // Busca a extension patient-animal que contém as informações veterinárias
        const animalExtension = patient.extension?.find(
          (ext: any) =>
            ext.url === "http://hl7.org/fhir/StructureDefinition/patient-animal"
        );

        // Extrai as sub-extensions de dentro da patient-animal
        const speciesExt = animalExtension?.extension?.find(
          (ext: any) => ext.url === "species"
        );
        const breedExt = animalExtension?.extension?.find(
          (ext: any) => ext.url === "breed"
        );
        const colorExt = animalExtension?.extension?.find(
          (ext: any) => ext.url === "animalColor"
        );
        const genderStatusExt = animalExtension?.extension?.find(
          (ext: any) => ext.url === "genderStatus"
        );

        // Extrai endereço (city e state)
        const address = patient.address?.[0];

        // Constrói a URL da foto a partir do base64 ou usa a URL externa
        const photoInfo = patient.photo?.[0];
        const photoUrl =
          photoInfo?.data && photoInfo?.contentType
            ? `data:${photoInfo.contentType};base64,${photoInfo.data}`
            : photoInfo?.url;

        // Extrai o ID do Binary da URL, se existir
        const photoBinaryId =
          photoInfo?.url && photoInfo.url.includes("Binary/")
            ? photoInfo.url.split("Binary/")[1]
            : undefined;

        return {
          resourceType: patient.resourceType || "Patient",
          id: patient.id || "",
          meta: patient.meta || {
            versionId: "1",
            lastUpdated: new Date().toISOString(),
            source: "",
          },
          identifier: patient.identifier || [],
          active: patient.active !== false,
          name: patient.name || [{ use: "official", text: "Pet" }],
          telecom: patient.telecom,
          gender: patient.gender || "unknown",
          birthDate: patient.birthDate || "",
          species:
            speciesExt?.valueCodeableConcept?.coding?.[0]?.code || "unknown",
          breed: breedExt?.valueCodeableConcept?.coding?.[0]?.display,
          color: colorExt?.valueCodeableConcept?.coding?.[0]?.display,
          neutered:
            genderStatusExt?.valueCodeableConcept?.coding?.[0]?.code ===
            "neutered",
          genderStatusCode:
            genderStatusExt?.valueCodeableConcept?.coding?.[0]?.code,
          photo: photoUrl,
          photoBinaryId: photoBinaryId,
          city: address?.city,
          state: address?.state,
        };
      })
      .filter((pet) => pet.active === true) || []; // Filtrar apenas pets ativos

  // Função para abrir o diálogo de edição de pet
  const handleEditPet = (pet: IPet) => {
    setSelectedPet(pet);

    // Busca o estado no UFValueSet baseado no código do state
    const stateOption = pet.state
      ? UFValueSet.find((uf) => uf.value === pet.state)
      : null;

    // Busca o objeto de cor completo
    const colorOption = pet.color
      ? VET_ANIMAL_COLORS.find((c) => c.display === pet.color)
      : null;

    // Busca o genderStatus completo
    const genderStatusOption = pet.genderStatusCode
      ? VET_GENDER_STATUS.find((gs) => gs.code === pet.genderStatusCode)
      : null;

    // Carrega a foto existente (se houver) para a pré-visualização no modal
    setPetPhotoPreviewUrl(pet.photo || null);
    setPetPhotoBase64(null); // Limpa o base64 para garantir que a foto só seja salva se for alterada
    setPhotoWasRemoved(false); // Reseta o estado de remoção da foto

    setPetForm({
      name: pet.name[0].text,
      birthDate: pet.birthDate ? dayjs(pet.birthDate) : null,
      color: colorOption || null,
      state: stateOption || null,
      city: pet.city || "",
      species: pet.species
        ? {
            code: pet.species,
            display: getSpeciesByCode(pet.species)?.display || pet.species,
          }
        : null,
      breed: pet.breed
        ? { code: pet.breed.toUpperCase(), display: pet.breed }
        : null,
      sex: pet.gender,
      genderStatus: genderStatusOption || null,
    });
    setDialogMode("edit");
    setOpenDialog(true);
  };

  // Função para abrir o modal de confirmação de exclusão
  const handleRemovePet = (pet: IPet) => {
    setPetToDelete(pet);
    setOpenDeleteDialog(true);
  };

  // Função para confirmar a exclusão do pet (soft delete)
  const confirmRemovePet = async () => {
    if (!petToDelete) return;

    setLoading(true);
    try {
      // Fazer PATCH no patient (animal) para marcar como inativo
      await PatchVetPatient({
        patientId: petToDelete.id,
        operation: "replace",
        path: "/active",
        content: false,
        authType: "user",
      });

      toast.success(`${petToDelete.name[0].text} foi removido com sucesso!`);

      // Fechar o modal
      setOpenDeleteDialog(false);
      setPetToDelete(null);

      // Recarregar a lista de pets
      if (userPatient) {
        await fetchRelatedData(userPatient);
      }
    } catch (error) {
      console.error("Erro ao remover pet:", error);
      toast.error("Erro ao remover pet. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  // Função para abrir o diálogo de adição de pet
  const handleAddPet = () => {
    setPetForm({
      name: "",
      birthDate: null,
      color: null,
      state: null,
      city: "",
      species: null,
      breed: null,
      sex: "",
      genderStatus: null,
    });
    setDialogMode("add");
    setOpenDialog(true);
    // Limpa os estados da foto para o modo de adição
    setPetPhotoBase64(null);
    setPetPhotoPreviewUrl(null);
    setPhotoWasRemoved(false);
  };

  const calculateAge = (birthDate: string) => {
    const today = dayjs();
    const birth = dayjs(birthDate);
    const years = today.diff(birth, "year");

    if (years === 0) {
      const months = today.diff(birth, "month");
      return `${months} ${months === 1 ? "mês" : "meses"}`;
    }

    return `${years} ${years === 1 ? "ano" : "anos"}`;
  };

  // Função para alternar expansão de detalhes do pet
  const toggleExpandPet = (petId: string) => {
    setExpandedPets((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(petId)) {
        newSet.delete(petId);
      } else {
        newSet.add(petId);
      }
      return newSet;
    });
  };

  // Função para salvar um pet (adicionar ou editar)
  const handleSavePet = async () => {
    setLoading(true);
    try {
      // Define qual foto será salva: a nova (petPhotoBase64) ou a existente (se não foi removida)
      const photoToSave =
        petPhotoBase64 ||
        (dialogMode === "edit" && !photoWasRemoved ? selectedPet?.photo : null);

      // Preparar os dados do pet no formato esperado pela API
      const vetPatientData: IVetPatientData = {
        name: petForm.name,
        gender: petForm.sex, // sex do formulário vai para gender no Patient
        ...(petForm.birthDate &&
          dayjs(petForm.birthDate).isValid() && {
            birthDate: petForm.birthDate,
          }),
        // Species: extrair code e display do objeto
        speciesCode: petForm.species?.code,
        speciesDisplay: petForm.species?.display,
        // Breed: extrair code (maiúsculo) e display do objeto
        breedCode: petForm.breed?.code,
        breedDisplay: petForm.breed?.display,
        // Color: extrair code e display do objeto
        colorCode: petForm.color?.code,
        colorDisplay: petForm.color?.display,
        // GenderStatus: extrair code e display do objeto
        genderStatusCode: petForm.genderStatus?.code,
        genderStatusDisplay: petForm.genderStatus?.display,
        // Localização
        city: petForm.city,
        state: petForm.state?.value,
        // No modo edit, incluir o id e identifier do pet original (obrigatórios no PUT)
        ...(dialogMode === "edit" &&
          selectedPet && {
            id: selectedPet.id,
            identifier: selectedPet.identifier,
          }),
      };

      // Adiciona ou remove o campo 'photo' explicitamente
      if (photoToSave) {
        vetPatientData.photo = photoToSave;
      } else if (dialogMode === "edit") {
        vetPatientData.photo = undefined; // Garante que o campo seja removido na atualização
      }

      if (dialogMode === "add") {
        // Criar novo pet
        const response = await PostVetPatient(vetPatientData);

        if (response && response.status === HTTPResponseStatus.Created) {
          toast.success("Pet cadastrado com sucesso!");

          // Criar RelatedPerson vinculando o pet ao tutor
          // CPF vem do user.username (usuário logado)
          const tutorCPF = user?.username || "";

          const responsibleData: IRelatedPersonData = {
            patient: {
              reference: `Patient/${response.data?.id}`,
              display: response.data?.name?.[0]?.text || "",
            },
            phoneNumber:
              userPatient?.telecom?.find((t: any) => t.system === "phone")
                ?.value || "",
            relationship: {
              system: "http://fluxmed.com.br/fhir/CodeSystem/parent-animal",
              code: "owner",
            },
            ownerName: userPatient?.name?.[0]?.text || "",
            gender: userPatient?.gender || "",
            cpfResponsible: tutorCPF,
          };

          const relatedPersonResponse = await PostRelatedPerson(
            responsibleData,
            {
              reference: `Patient/${response.data?.id}`,
              display: response.data?.name?.[0]?.text || "",
            },
            true,
            true
          );

          if (
            relatedPersonResponse &&
            relatedPersonResponse.status === HTTPResponseStatus.Success
          ) {
            // Adicionar link no Patient do tutor
            const existingLinks = userPatient?.link;
            const hasLink =
              Array.isArray(existingLinks) && existingLinks.length > 0;

            // CPF vem do user.username (sem formatação)
            const cpfIdentifier = user?.username;

            // Monta o patientId no formato esperado: {oid}-{cpf}
            const patientId = cpfIdentifier
              ? `${oids.cpf}-${cpfIdentifier}`
              : userPatient?.id;

            if (patientId) {
              await PatchPatient({
                patientId: patientId,
                operation: "add",
                path: hasLink ? "/link/-" : "/link",
                content: hasLink
                  ? {
                      other: {
                        reference: `RelatedPerson/${response.data?.id}`,
                      },
                    }
                  : [
                      {
                        other: {
                          reference: `RelatedPerson/${response.data?.id}`,
                        },
                      },
                    ],
                authType: "user",
              });
            } else {
              console.error(
                "Não foi possível encontrar o ID do paciente para o patch"
              );
              toast.warn(
                "Pet criado, mas link com tutor pode não ter sido estabelecido."
              );
            }
          }
        }
      } else if (dialogMode === "edit" && selectedPet) {
        // Atualizar pet existente
        const response = await PutVetPatient(vetPatientData, selectedPet.id);

        if (
          response &&
          (response.status === HTTPResponseStatus.Created ||
            response.status === HTTPResponseStatus.Success)
        ) {
          toast.success("Pet atualizado com sucesso!");
        }
      }

      // Recarrega a lista de pets após salvar
      if (userPatient) {
        await fetchRelatedData(userPatient);
      }

      // Limpar estado de foto após salvar
      setPetPhotoBase64(null);
      setPetPhotoPreviewUrl(null);
      setPhotoWasRemoved(false);

      setOpenDialog(false);
    } catch (error) {
      console.error("Erro ao salvar pet:", error);
      toast.error("Erro ao salvar pet. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  // Crop e seleção agora são controlados dentro do PetFormModal e devolvidos via callbacks

  // Função para atualizar o formulário
  const handleFormChange = (field: keyof IPetForm, value: any) => {
    setPetForm({
      ...petForm,
      [field]: value,
    });
  };

  // Função para validar se todos os campos obrigatórios estão preenchidos
  const isFormValid = () => {
    // Campos obrigatórios: name, species, sex, genderStatus, state
    return (
      petForm.name?.trim() !== "" &&
      petForm.species !== null &&
      petForm.sex !== "" &&
      petForm.genderStatus !== null &&
      petForm.state !== null
    );
  };

  // Exibe loading se estiver carregando dados ou executando ações
  if (loading || relatedLoading || downloadLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Pets sx={{ mr: 1 }} /> Meus Pets
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddPet}
            sx={{
              bgcolor: "#00A884",
              "&:hover": { bgcolor: "#008c6e" },
            }}
          >
            Novo Pet
          </Button>
        </Box>

        {pets.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Você ainda não cadastrou nenhum pet.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={handleAddPet}
              sx={{ mt: 2 }}
            >
              Adicionar Pet
            </Button>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {pets.map((pet) => (
              <Grid item xs={12} sm={6} md={4} key={pet.id}>
                <Card
                  // onClick={() => {
                  //   navigate("/summary", {
                  //     state: {
                  //       patientId: pet.identifier?.[0]?.value || pet.id,
                  //     },
                  //   });
                  // }}
                  sx={{
                    height: "100%",
                    // width: "fit-content",
                    transition: "transform 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent>
                    {/* Cabeçalho do card com informações do pet */}

                    <Stack
                      direction="column"
                      justifyContent="center"
                      alignItems="center"
                      spacing={1}
                    >
                      {/* Versão mobile: foto centralizada com nome e sexo abaixo */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          width: "100%",
                          mb: 2,
                        }}
                      >
                        <Avatar
                          src={pet.photo}
                          sx={{
                            width: 72,
                            height: 72,
                            mb: 1,
                            border: "2px solid",
                            borderColor:
                              pet.gender === "male" ? "#1976d2" : "#d81b60",
                          }}
                        >
                          {pet.species === "dog" ? "D" : "C"}
                        </Avatar>

                        <Stack
                          sx={{
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: "bold",
                              fontSize: "1rem",
                              textAlign: "center",
                            }}
                          >
                            {pet.name[0].text}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            {pet.gender === "male" ? (
                              <Male
                                fontSize="small"
                                sx={{ color: "#1976d2", mr: 0.5 }}
                              />
                            ) : (
                              <Female
                                fontSize="small"
                                sx={{ color: "#d81b60", mr: 0.5 }}
                              />
                            )}
                            <Typography variant="body2" color="text.secondary">
                              {pet.gender === "male" ? "Macho" : "Fêmea"}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>

                      <Stack spacing={1} sx={{ width: "100%" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <QrCodeIcon
                            fontSize="small"
                            sx={{ color: "#00A884", mr: 0.5 }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#00A884",
                              fontWeight: 600,
                              fontFamily: "monospace",
                              bgcolor: "rgba(0, 168, 132, 0.1)",
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                            }}
                          >
                            ID: {pet.identifier?.[0]?.value || pet.id}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, 1fr)",
                            justifyContent: "center",
                            mb: 1,
                            gap: 0.5,
                          }}
                        >
                          <Button
                            variant="outlined"
                            onClick={() => handlePreviewPetCard(pet)}
                            startIcon={<VisibilityIcon />}
                            sx={{
                              borderColor: "#00A884",
                              color: "#00A884",
                              minWidth: "auto",
                              "&:hover": {
                                backgroundColor: "rgba(0, 168, 132, 0.08)",
                                borderColor: "#00A884",
                              },
                            }}
                          >
                            Carteirinha
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={() => handleEditPet(pet)}
                            startIcon={<Edit />}
                            sx={{
                              borderColor: "#00A884",
                              color: "#00A884",
                              minWidth: "auto",
                              "&:hover": {
                                backgroundColor: "rgba(0, 168, 132, 0.08)",
                                borderColor: "#00A884",
                              },
                            }}
                          >
                            Editar
                          </Button>
                          <Button
                            variant="contained"
                            onClick={() => {
                              navigate("/summary", {
                                state: {
                                  patientId:
                                    pet.identifier?.[0]?.value || pet.id,
                                },
                              });
                            }}
                            startIcon={<DescriptionIcon />}
                            sx={{
                              bgcolor: "#00A884",
                              color: "#fff",
                              gridColumn: "1 / -1",
                              minWidth: "auto",
                              fontWeight: 600,
                              py: 1,
                              "&:hover": {
                                bgcolor: "#008c6e",
                              },
                            }}
                          >
                            Ver Registros
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={() => handleRemovePet(pet)}
                            startIcon={<Delete />}
                            sx={{
                              borderColor: "#f44336",
                              color: "#f44336",
                              gridColumn: "1 / -1",
                              minWidth: "auto",
                              "&:hover": {
                                backgroundColor: "rgba(244, 67, 54, 0.08)",
                                borderColor: "#f44336",
                              },
                            }}
                          >
                            Remover
                          </Button>
                        </Box>
                      </Stack>
                    </Stack>

                    <Divider sx={{ my: 1 }} />

                    <Stack spacing={1}>
                      {/* Informações básicas sempre visíveis */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Espécie:
                        </Typography>
                        <Typography variant="body2">
                          {getSpeciesByCode(pet.species)?.display ||
                            pet.species}
                        </Typography>
                      </Box>

                      {pet.breed && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Raça:
                          </Typography>
                          <Typography variant="body2">{pet.breed}</Typography>
                        </Box>
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Idade:
                        </Typography>
                        <Typography variant="body2">
                          {calculateAge(pet.birthDate)}
                        </Typography>
                      </Box>

                      {/* Informações expandidas com animação */}
                      <Collapse
                        in={expandedPets.has(pet.id)}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Stack spacing={1} sx={{ mt: 1 }}>
                          {pet.color && (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Cor:
                              </Typography>
                              <Typography variant="body2">
                                {pet.color}
                              </Typography>
                            </Box>
                          )}

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Castrado:
                            </Typography>
                            <Typography variant="body2">
                              {pet.genderStatusCode === "neutered"
                                ? "Sim"
                                : pet.genderStatusCode === "intact"
                                ? "Não"
                                : "Desconhecido"}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Data de Nascimento:
                            </Typography>
                            <Typography variant="body2">
                              {pet.birthDate && dayjs(pet.birthDate).isValid()
                                ? dayjs(pet.birthDate).format("DD/MM/YYYY")
                                : "-"}
                            </Typography>
                          </Box>

                          {pet.city && (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Município:
                              </Typography>
                              <Typography variant="body2">
                                {pet.city}
                              </Typography>
                            </Box>
                          )}

                          {pet.state && (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Estado:
                              </Typography>
                              <Typography variant="body2">
                                {UFValueSet.find((uf) => uf.value === pet.state)
                                  ?.label || pet.state}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </Collapse>
                    </Stack>

                    {/* Botão para expandir/colapsar detalhes */}
                    <Box sx={{ mt: 2, textAlign: "center" }}>
                      <Button
                        onClick={() => toggleExpandPet(pet.id)}
                        endIcon={
                          expandedPets.has(pet.id) ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )
                        }
                        sx={{
                          color: "#00A884",
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: "rgba(0, 168, 132, 0.08)",
                          },
                        }}
                      >
                        {expandedPets.has(pet.id)
                          ? "Menos detalhes"
                          : "Mais detalhes"}
                      </Button>
                    </Box>

                    {/* <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                   
                    variant="outlined"
                    onClick={() => navigate(`/pet/${pet.id}`)}
                    sx={{
                      borderColor: "#00A884",
                      color: "#00A884",
                      "&:hover": {
                        borderColor: "#008c6e",
                        backgroundColor: "rgba(0, 168, 132, 0.04)",
                      },
                    }}
                  >
                    Ver Detalhes
                  </Button>
                </Box> */}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Modal de Formulário de Pet - Usando componente reutilizável com design moderno */}

      {openDialog && (
        <PetFormModal
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          onSubmit={handleSavePet}
          mode={dialogMode}
          formData={petForm}
          onFormChange={handleFormChange}
          loading={loading}
          isValid={isFormValid()}
          initialPhotoUrl={selectedPet?.photo}
          photoPreviewUrl={petPhotoPreviewUrl}
          onPhotoChange={(base64, previewUrl) => {
            setPetPhotoBase64(base64);
            setPetPhotoPreviewUrl(previewUrl);
            setPhotoWasRemoved(false); // Se uma nova foto é adicionada, não está mais "removida"
          }}
          onPhotoRemove={() => {
            setPetPhotoBase64(null);
            setPetPhotoPreviewUrl(null);
            setPhotoWasRemoved(true);
          }}
        />
      )}

      {/* Modal de Confirmação de Exclusão - Usando componente reutilizável */}
      {openDeleteDialog && (
        <ConfirmDeleteModal
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          onConfirm={confirmRemovePet}
          loading={loading}
          itemName={petToDelete?.name[0].text || ""}
          itemType="Pet"
          title="Remover Pet"
          message="Tem certeza que deseja remover"
          infoMessage="O pet será desativado e não aparecerá mais na lista. Esta ação pode ser revertida posteriormente por um administrador."
        />
      )}

      {/* Pré-visualização da carteirinha do Pet */}

      {previewOpen && (
        <Dialog
          open={previewOpen}
          onClose={() => {
            setPreviewOpen(false);
            setPreviewImageUrl(null);
            setPreviewPet(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h6">Carteirinha do Pet</Typography>
              <IconButton
                onClick={() => {
                  setPreviewOpen(false);
                  setPreviewImageUrl(null);
                  setPreviewPet(null);
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 320,
              }}
            >
              {previewLoading ? (
                <CircularProgress />
              ) : previewImageUrl ? (
                <img
                  src={previewImageUrl}
                  alt="Pré-visualização da carteirinha"
                  style={{
                    width: "100%",
                    height: "auto",
                    borderRadius: 8,
                    border: "1px solid #e0e0e0",
                  }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Não foi possível gerar a pré-visualização.
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setPreviewOpen(false);
                setPreviewImageUrl(null);
                setPreviewPet(null);
              }}
            >
              Fechar
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                if (petCardData) {
                  try {
                    setDownloadLoading(true);
                    const canvas = await generatePetCard(petCardData);
                    const base64Image = canvas
                      .toDataURL("image/png")
                      .split(",")[1];
                    const fileName = `carteirinha-${petCardData.name
                      .replace(/\s+/g, "-")
                      .toLowerCase()}.png`;

                    downloadFile(base64Image, fileName, "image/png");

                    toast.success("Carteirinha baixada com sucesso!");
                  } catch (error) {
                    console.error("Erro ao gerar carteirinha:", error);
                    toast.error("Erro ao gerar carteirinha. Tente novamente.");
                  } finally {
                    setDownloadLoading(false);
                  }
                }
              }}
              disabled={previewLoading || !petCardData || downloadLoading}
              sx={{ bgcolor: "#00A884", "&:hover": { bgcolor: "#008c6e" } }}
              startIcon={
                downloadLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <DownloadIcon />
                )
              }
            >
              Baixar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {previewPet && (
        <div id={`qrcode-${previewPet.id}`} style={{ display: "none" }}>
          <QRCodeCanvas
            value={`${endpoints?.XDSB_domain}/portal/#/petaccess/${previewPet.id}`}
            size={256}
            bgColor="#ffffff"
            fgColor="#000000"
            level="L"
          />
        </div>
      )}
    </>
  );
};

export default MyPets;
